import React, { type FC } from 'react';
import { menuItems } from "./NavData";
import { useLocation } from 'react-router-dom';
import type { Admin, Customer } from '@univdiam/ui/type';
import { Plus } from 'lucide-react';

type BottomNavProps = {
  navigate: (to: string) => void;
  setCreateTicket: (open: boolean) => void;
  notificationCount: number;
  chatUnreadCount?: number;
  discussionUnreadCount?: number;
  user: Customer | Admin | null;
  isAdmin?: boolean;
};

const BottomNav: FC<BottomNavProps> = ({
  navigate,
  setCreateTicket,
  notificationCount,
  chatUnreadCount,
  discussionUnreadCount,
  user,
  isAdmin
}) => {
  const location = useLocation();

  // Define essential items for mobile, excluding Dashboard as it will be the first item
  // and we'll add the Create Order button in the middle.
  const essentialItems = menuItems.filter((item: any) => {
    const includedTexts = ["Dashboard", "Catalog", "All Chats"];
    if (isAdmin) {
      includedTexts.push("All Discussions");
    }
    return includedTexts.includes(item.text) && (!item.adminOnly || isAdmin);
  });

  // Split items to place Create Order in the middle: 2 items - Create - remaining items + Profile
  const firstHalf = essentialItems.slice(0, 2); // Dashboard, Catalog
  const secondHalf = essentialItems.slice(2); // All Chats, (All Discussions for Admin)
  
  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-t border-border/50 shadow-[0_-8px_20px_rgba(0,0,0,0.03)] flex justify-around items-center h-20 px-2 z-50 pb-safe">
      {firstHalf.map((item, index) => {
        const isActive = item.href === '/' 
          ? location.pathname === '/' 
          : location.pathname.startsWith(item.href);
        return (
          <button
            key={index}
            onClick={() => navigate(item.href)}
            className={`flex flex-col items-center justify-center w-full h-full space-y-1 transition-all duration-300 ${
              isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <div className="relative">
              {React.isValidElement(item.icon) && React.cloneElement(item.icon as React.ReactElement<any>, {
                size: 24,
                strokeWidth: isActive ? 2 : 1.5,
                className: `transition-all duration-300 ${isActive ? 'text-primary' : 'text-muted-foreground'}`
              })}
              {item.text === "All Chats" && (chatUnreadCount ?? 0) > 0 && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full" />
              )}
              {item.text === "All Discussions" && (discussionUnreadCount ?? 0) > 0 && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full" />
              )}
            </div>
            <span className={`text-[10px] truncate w-full text-center px-1 transition-all duration-300 ${isActive ? 'font-bold' : 'font-medium'}`}>
              {item.text}
            </span>
          </button>
        );
      })}

      {/* Central Create Order Button */}
      <div className="relative flex flex-col items-center justify-center w-full h-full -top-5">
        <button
          onClick={() => setCreateTicket(true)}
          className="bg-primary text-primary-foreground p-[1.1rem] rounded-full shadow-[0_8px_16px_rgba(0,0,0,0.15)] hover:shadow-[0_12px_20px_rgba(0,0,0,0.2)] active:scale-95 transition-all duration-300 ring-4 ring-white/90"
        >
          <Plus size={26} strokeWidth={2.5} />
        </button>
        <span className="text-[10px] font-bold text-primary mt-1.5 drop-shadow-sm">Create</span>
      </div>

      {secondHalf.map((item, index) => {
        const isActive = item.href === '/' 
          ? location.pathname === '/' 
          : location.pathname.startsWith(item.href);
        return (
          <button
            key={index}
            onClick={() => navigate(item.href)}
            className={`flex flex-col items-center justify-center w-full h-full space-y-1 transition-all duration-300 ${
              isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <div className="relative">
              {React.isValidElement(item.icon) && React.cloneElement(item.icon as React.ReactElement<any>, {
                size: 24,
                strokeWidth: isActive ? 2 : 1.5,
                className: `transition-all duration-300 ${isActive ? 'text-primary' : 'text-muted-foreground'}`
              })}
              {item.text === "All Chats" && (chatUnreadCount ?? 0) > 0 && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full" />
              )}
              {item.text === "All Discussions" && (discussionUnreadCount ?? 0) > 0 && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full" />
              )}
            </div>
            <span className={`text-[10px] truncate w-full text-center px-1 transition-all duration-300 ${isActive ? 'font-bold' : 'font-medium'}`}>
              {item.text === "All Chats" ? "Chats" : item.text}
            </span>
          </button>
        );
      })}
      
      {/* Profile circle as the last essential option */}
      <button
        onClick={() => navigate('/settings')}
        className={`flex flex-col items-center justify-center w-full h-full space-y-1 transition-all duration-300 ${
          location.pathname === '/settings' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        <div className={`w-6 h-6 rounded-full overflow-hidden border-2 transition-all duration-300 ${
          location.pathname === '/settings' ? 'border-primary shadow-[0_0_10px_rgba(var(--primary),0.3)]' : 'border-transparent'
        }`}>
          {user?.profile_pic ? (
            <img src={user.profile_pic} alt="profile" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-muted flex items-center justify-center text-[10px]">
              {user?.full_name?.charAt(0) || 'U'}
            </div>
          )}
        </div>
        <span className={`text-[10px] transition-all duration-300 ${location.pathname === '/settings' ? 'font-bold' : 'font-medium'}`}>Profile</span>
      </button>
    </nav>
  );
};

export default BottomNav;
